import { mapValues } from 'lodash';
import { stringify } from 'querystring';
import simpleRestProvider from 'ra-data-simple-rest';
import {
  DeleteManyParams,
  DeleteManyResult,
  DeleteParams,
  DeleteResult,
  GetListParams,
  GetManyParams,
  GetManyReferenceParams,
  GetManyResult,
  GetOneParams,
  GetOneResult,
  HttpError,
  Options,
  QueryFunctionContext,
  RaRecord,
  fetchUtils,
} from 'react-admin';
import type { DataProvider } from 'react-admin';
import { getAuth } from './AppAuthProvider';
import { bearerOf, decideAfterError } from './authInterceptor';
import { decideStepUpAfterError, getStepUpToken, STEP_UP_HEADER } from './stepUp';
import { isStepUpRoute } from './stepUpRoutes';
import { setSummary } from './SummaryStore';
import { getModuleDataProvider, hasModuleDataProvider } from './CustomDataProvider';

import { API_BASE_URL as baseUrl } from 'src/utils/api-url';
export { baseUrl };

export function modifyFilterParams(url: string) {
  const uri = new URL(url);
  const filterValue = uri.searchParams.get('filter');

  if (filterValue) {
    uri.searchParams.delete('filter');
    uri.searchParams.set('query', filterValue);
  }
  return uri.toString();
}

const countHeader = 'x-content-range';
export async function httpClient(_url: string, options: Options = {}) {
  const url = modifyFilterParams(_url);
  const { accessToken } = (await getAuth()) || {};

  const defaultHeaders = {
    Authorization: `Bearer ${accessToken}`,
    Accept: 'application/json',
    'Content-Type': 'application/json',
  };
  const headers = new Headers(options.headers);

  Object.entries(defaultHeaders).forEach(([key, value]) => {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  });

  // XBIT-4777: the step-up token goes only on the custody routes, and a header already present is never replaced.
  const stepUpToken = getStepUpToken();
  if (stepUpToken && isStepUpRoute(options.method, url) && !headers.has(STEP_UP_HEADER)) {
    headers.set(STEP_UP_HEADER, stepUpToken);
  }

  options.headers = headers;
  const send = (sendHeaders: Headers) =>
    fetchUtils.fetchJson(url, { ...options, headers: sendHeaders }).catch((err) => {
      if (err instanceof HttpError) {
        const { message, body, status } = err;
        throw new HttpError(message + '', status, body);
      }
      throw err;
    });

  const attempt = async (current: Headers, authRetried: boolean, stepUpRetried: boolean): Promise<Awaited<ReturnType<typeof send>>> => {
    try {
      return await send(current);
    } catch (err) {
      if (!(err instanceof HttpError)) throw err;
      // XBIT-4826 D10.2: a refused token is refreshed (or adopted from another tab) and the request resent once.
      if (!authRetried) {
        const next = await decideAfterError(err, { url, bearer: bearerOf(current.get('Authorization')), retried: false });
        if (next) {
          const retryHeaders = new Headers(current);
          retryHeaders.set('Authorization', `Bearer ${next}`);
          return attempt(retryHeaders, true, stepUpRetried);
        }
      }
      // XBIT-4777: a refused step-up is answered with a code and the request resent once with the new token.
      const token = await decideStepUpAfterError(err, {
        url,
        retried: stepUpRetried,
        skipStepUp: false,
        sentToken: current.get(STEP_UP_HEADER),
      });
      if (!token) throw err;
      const retryHeaders = new Headers(current);
      retryHeaders.set(STEP_UP_HEADER, token);
      return attempt(retryHeaders, authRetried, true);
    }
  };

  const res = await attempt(headers, false, false);

  if (res.headers.get(countHeader)) return res;
  const total = JSON.parse(res.body)?.total ?? 0;

  const responseHeaders = new Headers({
    ...res.headers,
    [countHeader]: total,
  });

  res.headers = responseHeaders;

  return res;
}

async function getPlayerById(playerId: string) {
  return httpClient(`/player/${playerId}`).then(({ json }) => json);
}

async function getList(resource: string, params: GetListParams) {
  // 检查是否有模块专用的数据提供者
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider) {
      return moduleProvider.getList(resource, params);
    }
  }

  // 使用默认的数据处理逻辑
  const { page = 1, perPage = 10 } = params.pagination || {};
  const { filter = {}, meta = {} } = params;
  const sort = JSON.stringify(params.sort);

  const search = toSearch({
    sort,
    page: page + '',
    size: perPage + '',
    query: JSON.stringify(filter),
    ...meta,
  });

  if (resource === 'user') {
    resource = 'users';
  }

  const rawResource = resource;

  const url = `${baseUrl}/${resource}${search}`;

  const { json } = await httpClient(url);

  const rows: any[] = json?.data ?? json?.list ?? json?.items ?? json?.records ?? [];
  const total = json?.total ?? json?.count ?? rows.length;

  if (json?.summary) {
    setSummary(rawResource, json?.summary);
  }

  const data = rows.map((r: any, i: number) => (r?.id != null ? r : { id: `${page}-${i}`, ...r }));

  return { data, total };

  // return httpClient(url).then(({ json }) => json);
}

function toSearch(params: Record<string, string | number>) {
  const searchParams = new URLSearchParams(mapValues(params, (v) => v.toString()));
  const search = searchParams.toString();
  return search ? '?' + search : '';
}

async function getManyReference(resource: string, params: GetManyReferenceParams) {
  const { page = 1, perPage = 10 } = params.pagination;

  const search = toSearch({
    sort: JSON.stringify(params.sort),
    page,
    size: perPage,
    query: JSON.stringify({
      ...params.filter,
      [params.target]: params.id,
    }),
  });

  const url = `${baseUrl}/${resource}?${search}`;

  return httpClient(url).then(({ json }) => json);
}

const dataProvider = simpleRestProvider(baseUrl, httpClient, countHeader);

const unwrapForRA = (json: any, fallbackId?: string | number) => {
  const payload = json?.data ?? json;
  if (payload && typeof payload === 'object' && 'id' in payload) {
    return { data: payload };
  }
  // Compatible with a few interfaces, place the id on the outermost layer
  if (json && typeof json === 'object' && 'id' in json && json.id) {
    return { data: { id: json.id } };
  }
  // At least return the id to avoid RA reporting an incorrect shape
  if (fallbackId != null) return { data: { id: fallbackId } };
  throw new Error('Invalid update response: missing data.id');
};

const getOne: DataProvider['getOne'] = (resource, params) => {
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider?.getOne) {
      return moduleProvider.getOne(resource, params);
    }
  }
  if (resource === 'unlimited-agents') {
    const url = `${baseUrl}/${resource}/${params.id}`;
    return httpClient(url).then(({ json }) => unwrapForRA(json, params.id));
  }
  return dataProvider.getOne(resource, params);
};

async function create(resource: string, params: any) {
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider?.create) {
      return moduleProvider.create(resource, params);
    }
  }
  if (resource === 'unlimited-agents') {
    const url = `${baseUrl}/${resource}`;
    return httpClient(url, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => {
      const payload = json?.data ?? json;
      if (payload?.id) return { data: payload };
      return { data: { id: payload?.id ?? params.data?.id ?? Date.now().toString() } };
    });
  }
  return dataProvider.create(resource, params);
}

async function update(resource: string, params: any) {
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider?.update) {
      return moduleProvider.update(resource, params);
    }
  }

  if (resource === 'unlimited-agents') {
    const url = `${baseUrl}/${resource}/${params.id}`;
    return httpClient(url, {
      method: 'PUT',
      body: JSON.stringify(params.data),
    }).then(({ json }) => unwrapForRA(json, params.id));
  }

  return dataProvider.update(resource, params);
}

async function _delete(resource: string, params: any) {
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider?.delete) {
      return moduleProvider.delete(resource, params);
    }
  }
  return dataProvider.delete(resource, params);
}

async function deleteMany(resource: string, params: any) {
  // BulkDeleteButton 会调用 deleteMany，这里拦截到模块实现
  if (hasModuleDataProvider(resource)) {
    const moduleProvider = getModuleDataProvider(resource);
    if (moduleProvider?.delete) {
      // 循环调用单条删除，聚合结果（后端暂未提供批量接口）
      const ids = params?.ids || [];
      const results = await Promise.all(
        ids.map((id: any) => moduleProvider.delete!(resource, { id }).then((res) => res.data))
      );
      return { data: results.map((r: any) => r?.id ?? r) };
    }
  }
  return (dataProvider as any).deleteMany(resource, params);
}

function getMany<RecordType extends RaRecord = any>(
  resource: string,
  params: GetManyParams<RecordType> & QueryFunctionContext
): Promise<GetManyResult<RecordType>> {
  const query = {
    filter: JSON.stringify({ id: params.ids }),
  };
  const url = `${baseUrl}/${resource}?${stringify(query)}`;
  return httpClient(url, { signal: params?.signal }).then(({ json }) => {
    return {
      data: json?.data || [],
    };
  });
}
async function customDelete<RecordType extends RaRecord = any>(
  resource: string,
  params: DeleteParams<RecordType>
): Promise<DeleteResult<RecordType>> {
  if (resource === 'token/onchain-infos') {
    const chainId = params.previousData?.chainId;

    if (!chainId) {
      throw new Error('Missing chainId in record for delete');
    }

    const customId = `${params.id}:${chainId}`;
    const url = `${baseUrl}/${resource}/${customId}`;

    return httpClient(url, { method: 'DELETE' }).then(({ json }) => ({ data: json }));
  }

  return dataProvider.delete(resource, params);
}

export async function getAuthHeaders(extra?: Record<string, string>) {
  const { accessToken } = (await getAuth()) || {};
  return {
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(extra ?? {}),
  };
}


const dp: DataProvider = {
  ...dataProvider,
  create,
  update,
  getOne,
  getMany,
  getList,
  getManyReference,
  getPlayerById,
  delete: _delete,
  deleteMany,
};

export default dp;
